import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { adminAlertStyle } from '../../styles/adminPageShell';

interface EvalRoleRow {
  id: number;
  code: string;
  displayName: string;
  enabled: boolean;
  isSystem: boolean;
  sortOrder: number;
}

export function EvalRolesSection() {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<EvalRoleRow | null>(null);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const { data: roles = [], isFetching } = useQuery<EvalRoleRow[]>({
    queryKey: ['eval-roles'],
    queryFn: () => api.get('/admin/eval-roles') as Promise<EvalRoleRow[]>,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['eval-roles'] });
  const onErr = (e: any) => message.error(e?.response?.data?.message || e?.message || '操作失败');

  const createMut = useMutation({
    mutationFn: (v: { code: string; displayName: string }) => api.post('/admin/eval-roles', v),
    onSuccess: () => {
      message.success('已新增评价角色');
      setAddOpen(false);
      addForm.resetFields();
      invalidate();
    },
    onError: onErr,
  });

  const updateMut = useMutation({
    mutationFn: (v: { id: number; displayName?: string; enabled?: boolean }) =>
      api.patch(`/admin/eval-roles/${v.id}`, { displayName: v.displayName, enabled: v.enabled }),
    onSuccess: () => {
      message.success('已保存');
      setEditing(null);
      editForm.resetFields();
      invalidate();
    },
    onError: onErr,
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/eval-roles/${id}`),
    onSuccess: () => {
      message.success('已删除');
      invalidate();
    },
    onError: onErr,
  });

  const openEdit = (r: EvalRoleRow) => {
    setEditing(r);
    editForm.setFieldsValue({ displayName: r.displayName });
  };

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={adminAlertStyle}
        message="评价角色"
        description={
          <ul style={{ margin: '4px 0 0 18px', padding: 0, fontSize: 12 }}>
            <li>评价角色可改名、可停用、可删除；可新增自定义角色：code 由 HR 手填（A-Z 0-9 _）。</li>
            <li>
              <strong>停用 = 全局总开关</strong>：停用后该角色在所有人才池整体失效——评价关系表不再显示该列、
              组织同步不再生成、随机/手工补派均被拦截（历史已提交的评价保留）。
            </li>
            <li>
              某个池要不要用某角色、权重多少 → 点「人才池 → 显示名」在本页表格增删改，或到「维度库」按池调整；
              谁担任该角色 → 由人才池「分组/职级」+ 花名册组织关系决定。
            </li>
          </ul>
        }
      />
      <Card
        style={{ marginTop: 16 }}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
            新增角色
          </Button>
        }
      >
        <Table<EvalRoleRow>
          rowKey="id"
          loading={isFetching}
          dataSource={roles}
          pagination={false}
          size="middle"
          columns={[
            {
              title: 'Code',
              dataIndex: 'code',
              width: 180,
              render: (v: string) => <Tag color="processing">{v}</Tag>,
            },
            { title: '显示名', dataIndex: 'displayName' },
            {
              title: '启用',
              dataIndex: 'enabled',
              width: 100,
              render: (v: boolean, r) => (
                <Switch
                  checked={v}
                  loading={updateMut.isPending}
                  onChange={(checked) => updateMut.mutate({ id: r.id, enabled: checked })}
                />
              ),
            },
            {
              title: '操作',
              key: 'op',
              width: 180,
              render: (_v, r) => (
                <Space>
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
                    改名
                  </Button>
                  <Popconfirm
                    title={`删除角色「${r.displayName}」？`}
                    description="未被派单使用的角色才可删除。"
                    onConfirm={() => removeMut.mutate(r.id)}
                  >
                    <Button size="small" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title="新增评价角色"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={() => addForm.submit()}
        confirmLoading={createMut.isPending}
        destroyOnClose
      >
        <Form
          form={addForm}
          layout="vertical"
          onFinish={(v) =>
            createMut.mutate({ code: v.code, displayName: (v.displayName || '').trim() })
          }
        >
          <Form.Item
            label="Code（A-Z 0-9 _，租户内唯一）"
            name="code"
            rules={[
              { required: true, message: 'code 必填' },
              { pattern: /^[A-Za-z0-9_]+$/, message: '只能包含字母、数字、下划线' },
            ]}
          >
            <Input placeholder="如 MENTOR" maxLength={32} />
          </Form.Item>
          <Form.Item
            label="显示名"
            name="displayName"
            rules={[{ required: true, message: '显示名必填' }]}
          >
            <Input placeholder="如 带教导师" maxLength={50} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`改名 · ${editing?.code ?? ''}`}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={() => editForm.submit()}
        confirmLoading={updateMut.isPending}
        destroyOnClose
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(v) =>
            editing && updateMut.mutate({ id: editing.id, displayName: (v.displayName || '').trim() })
          }
        >
          <Form.Item label="Code">
            <Input value={editing?.code} disabled />
          </Form.Item>
          <Form.Item
            label="显示名"
            name="displayName"
            rules={[{ required: true, message: '显示名必填' }]}
          >
            <Input maxLength={50} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
